import { TICK_MS, type Vec2, distance } from '@d2/core';

import { ENTITY_SIZE } from './art-spec';

/**
 * Where the camera looks, and the two rules that decide it.
 *
 * `docs/GAME-DESIGN.md` states both, and this module is the whole of them:
 *
 *  - **The camera follows the player, smoothly.** It eases toward the character
 *    each frame rather than being glued to it, which hides the 20 Hz simulation
 *    under 60 fps rendering and keeps a position correction from becoming a
 *    whole-screen jolt.
 *  - **Panning survives, but bounded: you can never pan further than your
 *    character.** Dragging peeks around — which a 128×128 floor wants — but the
 *    clamp means the character can never leave the view, and the camera eases
 *    back to centre.
 *
 * Pure and Phaser-free, for the same reason `./iso` is: a camera rule inside a
 * scene is a rule nothing can unit-test, and everything here is a `Vec2` in and
 * a `Vec2` out. The scene owns exactly two things this does not — reading
 * Phaser's camera and writing it back — and `./pointer-gesture` still owns
 * whether a press was a drag at all.
 *
 * Everything here is in **world pixels**: `./iso` has already turned cells into
 * them, and the clamp is a statement about a *screen*, so cells are the wrong
 * unit for it. A caller with a zoomed camera divides by the zoom on the way in
 * (`viewport`, `pan`) and nothing here needs to know that it did.
 *
 * ## What "never pan further than your character" means, exactly
 *
 * **The player's sprite stays wholly inside the viewport.** Not a radius in
 * world units and not a fraction of the map: the camera's centre may sit
 * anywhere within half a viewport of the player, less the margin the capsule
 * itself needs to be drawn in full.
 *
 * The three candidates differ where it matters and this is the one that answers
 * the sentence. A radius in world units says nothing about whether the body is
 * on screen — shrink the window and it is not. A rule stated against the *map*
 * (keep the camera inside the cave's bounds) is the one that traps a camera in a
 * corner, because at the edge of a 128×128 floor the two constraints fight and
 * something has to give. This one cannot trap anything: the permitted rectangle
 * is centred on the player and therefore always contains the player, so the
 * ease's own target is always reachable and no combination of edge, corner and
 * resize can leave the camera pinned somewhere it may not leave. The map is not
 * consulted at all, which is also why nothing here needs a `Grid`.
 *
 * It reads the viewport on every call rather than baking one in, so a resized
 * window changes how far you may peek — which is what "the character stays on
 * screen" means when the screen is a different size.
 *
 * ## The two motions, and why the drag suspends the ease
 *
 * A frame eases (`stepCamera`); a pointer move pans (`dragCamera`). While a drag
 * is *held* the ease stands down and the pan is one-to-one, so the world stays
 * pinned to the point the pointer grabbed — the property `./pointer-gesture`
 * pays six pixels to keep. An ease running underneath a drag would drag the
 * world back out from under the cursor while the hand was still moving it.
 * Letting go hands the camera back to the ease, which is the "eases back to
 * centre" half of the decision.
 *
 * A held camera is still *clamped* every frame, because the player can be part
 * way through a walk ordered before the drag began: the clamp then pushes the
 * camera along rather than re-centring it, and the peek survives as far as it
 * can. That is the one case where the two rules meet, and the bound wins.
 *
 * ## A teleport is a cut, not a pan
 *
 * A descent puts every seat on the new floor's entrance in the same handler that
 * rewrites the level (`descent.room.ts`, `advanceTo`), which on a 128×128 floor
 * is a jump of up to a hundred and eighty cells from wherever the stairs were.
 * Easing over that is a long slow pan across ground nobody travelled, so a
 * player who is no longer in view at all is **cut** to: one frame, no
 * interpolation, the shot recomposed. The predicate is the clamp's own, so there
 * is one definition of "in view" and no second threshold to keep in step with it
 * — and a free camera cannot get there any other way, since the pan clamps and
 * the ease only ever shortens the gap. A teleport that lands *inside* the view
 * is eased like anything else: nothing was lost, so nothing needs recomposing.
 *
 * A reconnection is the second one and lands the same way: the body has been
 * standing wherever it was left for up to the drop window, and the client that
 * comes back is seeded from the snapshot rather than from the entrance.
 *
 * Death itself asks nothing of the camera, and asks even less of it than it used
 * to. A dead player's body leaves the server's world and its last position stops
 * being written (`descent.room.ts`), which on this side is indistinguishable
 * from a player standing perfectly still — and a still player is a player the
 * camera has already settled on. Nothing then moves it: **the corpse does not
 * stand up any more.** The run ends a few seconds later, the seat leaves with
 * close code 4200 and the whole canvas comes down with it
 * (`./run-end`, `./game-canvas`), so the last thing this camera ever does on a
 * death is go on watching the place you fell.
 */

/** A camera's visible rectangle, in world pixels — `camera.width / zoom` by `camera.height / zoom`. */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/**
 * How fast the camera closes the gap: **half of whatever is left every two
 * simulation steps**.
 *
 * Expressed as a half-life rather than as a per-frame fraction so the follow is
 * a *rate*. A fraction of the gap per frame makes the camera's behaviour a
 * function of the frame rate — the same walk followed twice as tightly on a
 * machine drawing twice as many frames — and this suite's own host draws
 * anywhere between twenty and sixty (`TODO.md` #286).
 *
 * Two ticks is chosen against the thing the ease exists to hide. The simulation
 * moves the player 0.2 cells every 50 ms and nothing between those steps, which
 * at this projection is a jump of about seven pixels twenty times a second; a
 * camera that lags by roughly the same order smooths the sequence into a drift
 * without ever putting the character anywhere but the middle of the screen. It
 * works out at about twenty pixels of lag at walking pace — a third of a tile,
 * visible as smoothness and not as delay.
 */
export const CAMERA_FOLLOW_HALF_LIFE_MS = 2 * TICK_MS;

/**
 * How far inside the viewport the player's feet must stay, in world pixels.
 *
 * The furthest the sprite reaches from the position it is drawn at, taken from
 * the art rather than typed in: the capsule has a bottom-centre origin, so it
 * covers `ENTITY_SIZE.height` above its feet and half its width to either side.
 * One number for all four edges — exact at the top, where the sprite is tallest,
 * and generous at the sides — because a margin per edge would be four numbers
 * to keep true of a placeholder that is going to be replaced by real art
 * (`TODO.md` #9).
 */
export const CAMERA_EDGE_MARGIN_PX = Math.max(ENTITY_SIZE.height, ENTITY_SIZE.width / 2);

/**
 * How close is close enough to stop: **half a pixel**.
 *
 * An exponential never arrives, and a camera that never arrives is not a
 * cosmetic problem. Sub-pixel motion is invisible but it is still motion: the
 * scroll changes every frame forever, so "the camera has come to rest" stops
 * being a thing anything can wait for, and a click's page position — measured
 * against the scroll, spent a frame later — is never quite aimed where it was
 * measured. Half a pixel is below what the screen can show and below what a
 * pointer can mean.
 */
export const CAMERA_SETTLE_PX = 0.5;

/** How far the camera's centre may sit from the player, per axis, in world pixels. */
export const peekLimit = (viewport: Viewport): Vec2 => ({
  x: Math.max(0, viewport.width / 2 - CAMERA_EDGE_MARGIN_PX),
  y: Math.max(0, viewport.height / 2 - CAMERA_EDGE_MARGIN_PX),
});

/**
 * `true` when a camera looking at `centre` draws the whole of the player.
 *
 * Floored at zero rather than allowed to go negative, so a window smaller than
 * the sprite pins the camera to the player instead of inverting the clamp into a
 * rectangle nothing can satisfy.
 */
export const showsPlayer = (centre: Vec2, player: Vec2, viewport: Viewport): boolean => {
  const limit = peekLimit(viewport);

  return Math.abs(centre.x - player.x) <= limit.x && Math.abs(centre.y - player.y) <= limit.y;
};

const clampAxis = (value: number, around: number, limit: number): number =>
  Math.min(around + limit, Math.max(around - limit, value));

/**
 * The nearest place to `centre` the camera is allowed to look — the clamp
 * itself.
 *
 * Per axis, so a peek that ran out of room downward keeps everything it had
 * sideways. The player is always inside the result, which is what makes this
 * incapable of trapping a camera anywhere.
 */
export const clampToPlayer = (centre: Vec2, player: Vec2, viewport: Viewport): Vec2 => {
  const limit = peekLimit(viewport);

  return {
    x: clampAxis(centre.x, player.x, limit.x),
    y: clampAxis(centre.y, player.y, limit.y),
  };
};

/**
 * One frame of easing: half the remaining gap per half-life, and exactly the
 * target once what is left is not worth drawing.
 *
 * `2 ** (-frameMs / halfLife)` is the fraction of the gap that survives a frame,
 * so two frames of *t* leave exactly what one frame of *2t* leaves and a stalled
 * frame catches up by however much it was worth. A frame that took no time
 * spends nothing, which also disposes of the negative deltas a clock adjustment
 * can hand a browser.
 */
export const easeTowards = (from: Vec2, to: Vec2, frameMs: number, halfLifeMs = CAMERA_FOLLOW_HALF_LIFE_MS): Vec2 => {
  if (frameMs <= 0) return from;

  const remaining = 2 ** (-frameMs / halfLifeMs);
  const eased = {
    x: to.x + (from.x - to.x) * remaining,
    y: to.y + (from.y - to.y) * remaining,
  };

  return distance(eased, to) < CAMERA_SETTLE_PX ? to : eased;
};

/** One frame's worth of camera: where it is looking now, where the player is, and how long the frame was. */
export interface CameraStep {
  /** Where the camera is looking, in world pixels. */
  readonly centre: Vec2;
  /**
   * Where the local player is — the **predicted** position and never the
   * interpolated one.
   *
   * `session.ts` draws the local player out of `./prediction` and everybody else
   * out of a buffer that deliberately runs ~100 ms behind. A camera pointed at
   * the second of those would put that delay on the whole world, which is
   * exactly the input lag prediction exists to avoid (`TODO.md` #162) and would
   * be invisible in a screenshot.
   */
  readonly player: Vec2;
  readonly viewport: Viewport;
  /** How long this frame was, in milliseconds. Phaser's own delta. */
  readonly frameMs: number;
  /** `true` while a drag is in progress: the pointer owns the camera and the ease waits. */
  readonly held: boolean;
}

/**
 * Where the camera should look after this frame.
 *
 * Three outcomes, and the whole of the follow policy:
 *
 *   - **A drag is holding it.** The pan already moved it; all this does is keep
 *     the bound true against a player who is still walking.
 *   - **The player is off screen.** Only a teleport can produce that, so the
 *     camera cuts rather than panning across ground nobody crossed.
 *   - **Anything else.** Ease, which converges on the player and never leaves
 *     the view it started inside.
 */
export const stepCamera = ({ centre, player, viewport, frameMs, held }: CameraStep): Vec2 => {
  if (held) return clampToPlayer(centre, player, viewport);
  if (!showsPlayer(centre, player, viewport)) return player;

  return easeTowards(centre, player, frameMs);
};

/** One pointer move's worth of pan: the ground the drag covered, and who it may not lose sight of. */
export interface CameraDrag {
  readonly centre: Vec2;
  readonly player: Vec2;
  readonly viewport: Viewport;
  /** World pixels the pointer travelled this move — `./pointer-gesture`'s pan, divided by the zoom. */
  readonly pan: Vec2;
}

/**
 * Drag the world: the camera moves against the pointer, one for one, and stops
 * dead at the clamp.
 *
 * Clamped here as well as on the next frame so the bound holds *within* a
 * gesture rather than a frame later — a fast drag can deliver several moves
 * between two frames, and the camera must never have been anywhere the player
 * was off screen, not merely never be drawn there.
 */
export const dragCamera = ({ centre, player, viewport, pan }: CameraDrag): Vec2 =>
  clampToPlayer({ x: centre.x - pan.x, y: centre.y - pan.y }, player, viewport);
